import { reactive } from 'vue';
import { CHAT_TYPE, GENERAL_ORDER_CODE, isChatTypeId } from './channels';
import { sameBusinessCodes } from './chatIdentity';
import { parseChatPush } from './chatPush';

const initialState = () => ({
	available: false,
	connecting: false,
	needsLogin: false,
	businessCodes: null,
	openChats: [],
	popups: [],
	focusedPopupKey: null,
	drawerOpen: false,
	rateQueue: [],
	rateRequest: null,
});

// rateAsked: the chats this page already asked to rate, kept across logins like the page itself.
export const chatStore = reactive({
	...initialState(),
	teardowns: 0,
	viewedOrderCode: null,
	pendingOpen: null,
	rateAsked: new Set(),
});

let popupSeq = 0;

const nonEmptyString = (value) => (typeof value === 'string' && value !== '' ? value : null);

export const resetChatState = () => {
	Object.assign(chatStore, initialState());
};

export const clearChatConversations = () => {
	chatStore.businessCodes = null;
	chatStore.openChats = [];
	chatStore.popups = [];
	chatStore.focusedPopupKey = null;
};

export const setChatIdentity = (businessCodes) => {
	if (!sameBusinessCodes(businessCodes, chatStore.businessCodes)) {
		clearChatConversations();
		chatStore.businessCodes = [...businessCodes];
	}
	chatStore.available = true;
	chatStore.connecting = false;
	chatStore.needsLogin = false;
};

export const setOpenChats = (chats, forBusinessCodes) => {
	if (forBusinessCodes !== undefined && !sameBusinessCodes(forBusinessCodes, chatStore.businessCodes)) {
		return false;
	}
	chatStore.openChats = chats;
	return true;
};

export const chatPhase = () => {
	if (!chatStore.available) {
		return 'off';
	}
	if (chatStore.businessCodes) {
		return 'ready';
	}
	return chatStore.needsLogin ? 'needsLogin' : 'connecting';
};

export const setViewedOrderCode = (orderCode) => {
	chatStore.viewedOrderCode = nonEmptyString(orderCode);
};

export const totalUnread = () => chatStore.openChats.reduce((total, chat) => total + chat.unreadCount, 0);

export const chatsForOrder = (orderCode) => chatStore.openChats.filter((chat) => chat.orderCode === String(orderCode));

export const findOpenChat = ({ typeId, chatId, key, orderCode } = {}) => {
	const type = Number(typeId);
	const id = nonEmptyString(chatId);
	const pushKey = nonEmptyString(key);
	const code = orderCode == null ? null : String(orderCode);
	return (
		chatStore.openChats.find((chat) => {
			if (chat.typeId !== type) {
				return false;
			}
			if (id) {
				return chat.chatId === id;
			}
			if (pushKey) {
				return chat.key === pushKey;
			}
			return code != null && chat.orderCode === code;
		}) || null
	);
};

export const generalSupportChat = () => findOpenChat({ typeId: CHAT_TYPE.SUPPORT, orderCode: GENERAL_ORDER_CODE });

export const hasSeveralRestaurants = (user) => Array.isArray(user?.restaurants) && user.restaurants.length > 1;

const popupByKey = (key) => chatStore.popups.find((popup) => popup.key === key) || null;

const samePopupTarget = (popup, target) => {
	if (popup.typeId !== target.typeId) {
		return false;
	}
	if (popup.chatId && target.chatId) {
		return popup.chatId === target.chatId;
	}
	if (popup.chatKey && target.chatKey) {
		return popup.chatKey === target.chatKey;
	}
	return popup.orderCode != null && popup.orderCode === target.orderCode;
};

const focus = (popup) => {
	popup.minimized = false;
	chatStore.focusedPopupKey = popup.key;
	return popup.key;
};

export const openChat = (target) => {
	const typeId = Number(target?.typeId);
	if (!chatStore.businessCodes || !isChatTypeId(typeId)) {
		return null;
	}
	const listed = findOpenChat({ ...target, typeId });
	const wanted = {
		typeId,
		chatId: nonEmptyString(target.chatId) || listed?.chatId || null,
		orderCode: target.orderCode != null ? String(target.orderCode) : listed?.orderCode ?? null,
		chatKey: nonEmptyString(target.key) || listed?.key || null,
	};
	if (!wanted.chatId && !wanted.chatKey && wanted.orderCode == null) {
		return null;
	}
	const existing = chatStore.popups.find((popup) => samePopupTarget(popup, wanted));
	if (existing) {
		existing.chatId = existing.chatId || wanted.chatId;
		return focus(existing);
	}
	popupSeq += 1;
	const popup = { key: `chat-popup-${popupSeq}`, ...wanted, minimized: false };
	chatStore.popups.push(popup);
	return focus(popupByKey(popup.key));
};

export const resolvePopupChat = (key, chatId) => {
	const popup = popupByKey(key);
	if (!popup || !nonEmptyString(chatId)) {
		return null;
	}
	const twin = chatStore.popups.find((p) => p !== popup && p.typeId === popup.typeId && p.chatId === chatId);
	if (twin) {
		closePopup(key);
		return focus(twin);
	}
	popup.chatId = chatId;
	return key;
};

export const closePopup = (key) => {
	chatStore.popups = chatStore.popups.filter((popup) => popup.key !== key);
	if (chatStore.focusedPopupKey === key) {
		chatStore.focusedPopupKey = null;
	}
};

export const minimizePopup = (key) => {
	const popup = popupByKey(key);
	if (popup) {
		popup.minimized = true;
		if (chatStore.focusedPopupKey === key) {
			chatStore.focusedPopupKey = null;
		}
	}
};

export const restorePopup = (key) => {
	const popup = popupByKey(key);
	return popup ? focus(popup) : null;
};

export const focusPopup = (key) => {
	if (popupByKey(key)) {
		chatStore.focusedPopupKey = key;
	}
};

export const blurPopup = (key) => {
	if (chatStore.focusedPopupKey === key) {
		chatStore.focusedPopupKey = null;
	}
};

export const isChatWindowFocused = ({ typeId, chatId }) =>
	chatStore.popups.some(
		(popup) =>
			popup.key === chatStore.focusedPopupKey &&
			!popup.minimized &&
			popup.typeId === typeId &&
			popup.chatId === chatId
	);

export const toggleDrawer = (open) => {
	chatStore.drawerOpen = typeof open === 'boolean' ? open : !chatStore.drawerOpen;
};

const pendingTargetOf = (input) => {
	const source = input && typeof input === 'object' && 'channelName' in input ? parseChatPush(input) : input;
	if (!source || typeof source !== 'object' || source.closed) {
		return null;
	}
	if (source.notificationType != null && parseInt(String(source.notificationType), 10) !== 0) {
		return null;
	}
	const typeId = source.typeId != null && source.typeId !== '' ? Number(source.typeId) : null;
	const chatId = nonEmptyString(source.chatId) || nonEmptyString(source.docId);
	const key = nonEmptyString(source.key);
	return isChatTypeId(typeId) && (chatId || key) ? { typeId, chatId, key } : null;
};

export const setPendingOpen = (input) => {
	const target = pendingTargetOf(input);
	if (target) {
		chatStore.pendingOpen = target;
	}
	return !!target;
};

// A chat's close can reach this page more than once (its open window sees it, a rate push follows), so a
// chat is asked about once; one dialog shows at a time and the rest wait in line.
export const askToRateChat = ({ typeId, chatId, message } = {}) => {
	const type = Number(typeId);
	const id = nonEmptyString(chatId);
	if (!isChatTypeId(type) || !id || chatStore.rateAsked.has(id)) {
		return false;
	}
	chatStore.rateAsked.add(id);
	chatStore.rateQueue.push({ typeId: type, chatId: id, message: nonEmptyString(message) || '' });
	return true;
};

const rateTargetOf = (input) => {
	const source = input && typeof input === 'object' && 'channelName' in input ? parseChatPush(input) : input;
	if (!source || typeof source !== 'object') {
		return null;
	}
	const closed =
		source.closed || (source.notificationType != null && parseInt(String(source.notificationType), 10) !== 0);
	return closed ? { typeId: source.typeId, chatId: source.chatId || source.docId, message: source.body } : null;
};

// Where a chat push's click or its landing link leads: an open chat opens, a closed one asks for its rating.
export const followChatLink = (input) => {
	if (setPendingOpen(input)) {
		return true;
	}
	const target = rateTargetOf(input);
	return !!target && askToRateChat(target);
};

export const takeRateRequest = () => (chatStore.rateRequest ? null : chatStore.rateQueue.shift() || null);

export const showRateRequest = (request) => {
	chatStore.rateRequest = request;
};

export const closeRateRequest = () => {
	chatStore.rateRequest = null;
};

export const consumePendingOpen = () => {
	const target = chatStore.pendingOpen;
	if (!target || !chatStore.businessCodes) {
		return null;
	}
	chatStore.pendingOpen = null;
	return openChat(target);
};

export default chatStore;
